import * as Sentry from '@sentry/react';

import { MAP_GEOLOCATION_ZOOM } from '~/components/ModalDeliveryAddress/constants';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';

import { mainStore } from '~/stores';

interface GeolocationResponse {
  location: {
    coords: {
      latitude: number;
      longitude: number;
    };
  };
}

interface GeolocationError {
  message?: string;
}

interface UseGeolocationReturn {
  requestLocation: () => Promise<void>;
}

export const useGeolocation = (): UseGeolocationReturn => {
  const requestLocation = async () => {
    if (deliveryAddressStore.isActiveGeolocation) {
      deliveryAddressStore.setIsActiveGeolocation(false);
    }
    deliveryAddressStore.setIsAwaitGeolocation(true);
    deliveryAddressStore.setIsMapBusy(true);

    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    let hasError = false;

    if (mainStore.isRN) {
      const MAX_GEOLOCATION_ATTEMPTS = 6;
      const getGeolocation = () =>
        new Promise<GeolocationResponse>((resolve, reject) => {
          mainStore.sendToRN(
            'getGeolocation',
            {
              target: 'findme_icon',
            },
            (e) => {
              if (
                !e ||
                !e.location?.coords?.latitude ||
                !e.location?.coords?.longitude
              ) {
                reject(e);
              } else {
                resolve(e as GeolocationResponse);
              }
            },
            (e) => {
              hasError = true;
              deliveryAddressStore.setIsMapBusy(false);
              deliveryAddressStore.setIsAwaitGeolocation(false);
              reject(e);
            },
          );
        });

      for (let i = 0; i < MAX_GEOLOCATION_ATTEMPTS; i++) {
        if (hasError) {
          break;
        }

        try {
          const result = await getGeolocation();
          deliveryAddressStore.setGeolocationCoordinates({
            lat: result.location.coords.latitude,
            lng: result.location.coords.longitude,
          });
          deliveryAddressStore.setMapZoom(MAP_GEOLOCATION_ZOOM);
          deliveryAddressStore.setIsActiveGeolocation(true);
          deliveryAddressStore.setIsMapBusy(false);
          deliveryAddressStore.setIsSmallSizeCover(true);
          deliveryAddressStore.setIsAwaitGeolocation(false);
          // alert(JSON.stringify(result));
          break;
        } catch (error) {
          const e = error as GeolocationError;
          // alert(JSON.stringify(e));
          if (i === MAX_GEOLOCATION_ATTEMPTS - 1) {
            deliveryAddressStore.setIsActiveGeolocation(false);
            deliveryAddressStore.setGeolocationCoordinates(null);
            deliveryAddressStore.setIsAwaitGeolocation(false);
            deliveryAddressStore.setIsMapBusy(false);

            Sentry.withScope((scope) => {
              scope.setExtras({
                message: e.message || undefined,
                error: e,
              });
              Sentry.captureMessage('Auto Geolocation error', 'error');
            });
          }
        }
      }
    } else {
      if (!navigator.geolocation) {
        deliveryAddressStore.setIsMapBusy(false);
        deliveryAddressStore.setIsAwaitGeolocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (e) => {
          if (!e || !e.coords?.latitude || !e.coords?.longitude) {
            deliveryAddressStore.setIsMapBusy(false);
            deliveryAddressStore.setIsAwaitGeolocation(false);
            return;
          }
          deliveryAddressStore.setGeolocationCoordinates({
            lat: e.coords.latitude,
            lng: e.coords.longitude,
          });
          deliveryAddressStore.setMapZoom(MAP_GEOLOCATION_ZOOM);
          deliveryAddressStore.setIsActiveGeolocation(true);
          deliveryAddressStore.setIsMapBusy(false);
          deliveryAddressStore.setIsSmallSizeCover(true);
          deliveryAddressStore.setIsAwaitGeolocation(false);
          deliveryAddressStore.setDeliveryAddress(null);
        },
        (err) => {
          // biome-ignore lint/suspicious/noConsole: console.log is used to log the geolocation error
          console.log('Geolocation error: ' + err.message);
          deliveryAddressStore.setIsActiveGeolocation(false);
          deliveryAddressStore.setGeolocationCoordinates(null);
          deliveryAddressStore.setIsAwaitGeolocation(false);
          deliveryAddressStore.setIsMapBusy(false);

          Sentry.withScope((scope) => {
            scope.setExtras({
              message: err.message || undefined,
              error: err,
            });
            Sentry.captureMessage('Auto Geolocation error', 'error');
          });
        },
        options,
      );
    }
  };

  deliveryAddressStore.setIsManuallyInput(false);
  deliveryAddressStore.setInputAddressFocused(false);
  return { requestLocation };
};
